import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const monacoEPrixEn: ChauffeurPage = {
  slug: "monaco-e-prix",
  title: "Private chauffeur Monaco E-Prix (Formula E) – VIP VTC",
  subtitle:
    "Private chauffeur during the Monaco E-Prix: street-circuit access, Nice airport meet-and-greet and daily hire during Formula E.",
  keywords: [
    "monaco e-prix chauffeur",
    "formula e monaco chauffeur",
    "monaco eprix vtc",
  ],
  hero: {
    title: "Private chauffeur during the Monaco E-Prix",
    subtitle:
      "Formula E on the Monaco street circuit: a dedicated chauffeur when the streets close and the paddock is unreachable by car.",
    keywords: [
      "Monaco E-Prix chauffeur",
      "Formula E VTC",
      "Monaco street circuit chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur during the Monaco E-Prix",
    badges: ["VIP airport welcome", "Street-circuit access", "Hourly hire"],
  },
  benefits: {
    heading: "Why a chauffeur during the Monaco E-Prix",
    items: [
      {
        title: "Street circuit, closed roads",
        description:
          "In May, Formula E closes part of Monaco's streets around the circuit. The chauffeur knows the open access routes and the drop-offs nearest the gates.",
      },
      {
        title: "Nice airport welcome",
        description:
          "Nice Côte d'Azur airport is about thirty minutes away. Name-board welcome, flight tracking and a direct transfer to your hotel.",
      },
      {
        title: "Paddock and hospitality",
        description:
          "Pick-ups timed around practice, the race and e-Village / paddock access, with no parking to manage.",
      },
      {
        title: "Late-night returns secured",
        description:
          "Carré d'Or dinners and after-race parties: a chauffeur waits and guarantees the late return.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services during the Monaco E-Prix",
    services: [
      {
        title: "Nice airport meet-and-greet",
        text: "Nice ↔ Monaco transfer with flight tracking across the Formula E weekend.",
      },
      {
        title: "Street-circuit access",
        text: "Drop-off as close as possible to the gates within the street closures around the circuit.",
      },
      {
        title: "Full-day hire",
        text: "A dedicated chauffeur to chain practice, race, paddock and meetings.",
      },
      {
        title: "Evenings & after-race",
        text: "Carré d'Or restaurants and clubs, with a guaranteed late-night return.",
      },
      {
        title: "Partner & team shuttles",
        text: "Multi-vehicle coordination for sponsors, guests and delegations.",
      },
    ],
  },
  locations: {
    heading: "Key points during the Monaco E-Prix",
    items: [
      {
        name: "Monaco street circuit",
        description: "Layout around Port Hercule, the Casino and the seafront.",
      },
      {
        name: "Port Hercule & e-Village",
        description: "Paddock, hospitality and partner areas.",
      },
      {
        name: "Carré d'Or & Casino",
        description: "Hôtel de Paris, Hermitage and Place du Casino.",
      },
      {
        name: "Nice Côte d'Azur airport",
        description: "Terminals 1 and 2, about thirty minutes away.",
      },
      {
        name: "Beausoleil & heights",
        description: "Residences and access from around Monaco.",
      },
    ],
  },
  generic: {
    heading: "The Monaco E-Prix from a chauffeur's seat",
    subheading:
      "In May, Formula E takes over the Monaco street circuit and closes part of the centre's roads.",
    paragraphs: [
      "Every May, the Monaco E-Prix brings Formula E to the Monaco street circuit, partly shared with the Grand Prix layout. Roads close around Port Hercule and the Casino: a chauffeur who knows the still-open routes drops you as close as possible to your gate.",
      "Arrivals concentrate at Nice Côte d'Azur airport, about thirty minutes away. With flight tracking and a name-board welcome, we absorb delays to take you straight to your Monaco hotel.",
      "The weekend chains practice, the electric race and e-Village hospitality. Hourly hire keeps the same chauffeur all day and secures the late-night returns after the parties.",
    ],
    bullets: [
      "Drop-off nearest the circuit",
      "Flight tracking in May",
      "After-race late-night returns",
    ],
  },
  process: {
    heading: "Booking a chauffeur for the Monaco E-Prix",
    steps: [
      "Tell us your dates, your hotel and your access (grandstands, paddock, e-Village).",
      "We plan the routes around the street closures near the circuit.",
      "The chauffeur arrives in uniform, with flight tracking for your Nice arrivals.",
      "Direct transfer or hourly hire to cover practice, race and evenings.",
    ],
  },
  faq: [
    {
      q: "How do I book a private chauffeur for the Monaco E-Prix?",
      a: "Book early: in May, Monaco is in high demand during Formula E. Share your dates, your hotel and your access so we can secure a dedicated chauffeur across the weekend.",
    },
    {
      q: "Can you get me near the circuit despite the closed streets?",
      a: "Yes. The street circuit closes part of the centre. The chauffeur uses the still-open routes and drops you at the nearest authorised point to your gate.",
    },
    {
      q: "Do you handle arrivals from Nice airport?",
      a: "Yes, with flight tracking and a name-board welcome. Nice Côte d'Azur airport is about thirty minutes away; we adjust the pick-up if your flight is delayed.",
    },
    {
      q: "Do you offer full-day hire?",
      a: "Yes. A dedicated chauffeur stays at your disposal to chain practice, race and paddock without managing Monaco parking.",
    },
    {
      q: "Do you cover returns after the after-race parties?",
      a: "Yes. Carré d'Or evenings end late: the chauffeur waits and guarantees your late-night return.",
    },
    {
      q: "Why choose KS Rent Services for the Monaco E-Prix?",
      a: "Our private chauffeur service on the French Riviera is rated 5.0/5 from 38 Google reviews: a uniformed, punctual chauffeur used to Monaco's access constraints.",
    },
  ],
  relatedLinks: [
    { label: "Airport transfers in Monaco", href: "/en/chauffeur/monaco" },
    { label: "Private chauffeur in Nice", href: "/en/chauffeur/nice" },
    {
      label: "Private car service at Nice Airport",
      href: "/en/chauffeur/airport",
    },
    { label: "Car hire in Monaco", href: "/en/location/car-rental-monaco" },
  ],
  cta: {
    title: "Your chauffeur for the Monaco E-Prix",
    text: "Circuit access, airport welcome and evenings: book a dedicated chauffeur for the Formula E weekend.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Monaco E-Prix Chauffeur — Formula E VIP car service",
    description:
      "Private chauffeur during the Monaco E-Prix (Formula E): street-circuit access, Nice airport meet-and-greet, hourly hire and late-night returns. Book now.",
    canonical: "/en/chauffeur/monaco-e-prix",
  },
};

export default monacoEPrixEn;
